"use client";

import {
  Grid,
  GridItem,
  type GridItemSpanValue,
} from "@boostdev/design-system-components/client";
import type { Photo } from "@/lib/immich/types";
import type { PhotoDayGroup } from "@/lib/immich/dayGroups";
import { PhotoThumbnail } from "./PhotoThumbnail";
import css from "./GalleryDaySection.module.css";

export interface GalleryDaySectionProps {
  readonly group: PhotoDayGroup;
  /** Localised heading above the group's grid; null renders no heading. */
  readonly heading: string | null;
  /** Linear full-width layout below the tablet breakpoint. */
  readonly isNarrow: boolean;
  readonly onOpen: (index: number) => void;
  readonly liveBadgeLabel: string;
  readonly videoBadgeLabel: string;
}

function photoColumnSpan(photo: Photo): GridItemSpanValue {
  if (photo.width <= 0 || photo.height <= 0) return "one-quarter";
  const ratio = photo.width / photo.height;
  if (ratio > 3.5) return "full";
  if (ratio > 2.2) return "three-quarters";
  if (ratio > 1.5) return "half";
  return "one-quarter";
}

export function GalleryDaySection({
  group,
  heading,
  isNarrow,
  onOpen,
  liveBadgeLabel,
  videoBadgeLabel,
}: GalleryDaySectionProps) {
  return (
    <section className={css.section}>
      {heading !== null ? <h3 className={css.heading}>{heading}</h3> : null}
      <Grid isMasonry={!isNarrow} variant="main">
        {group.items.map(({ photo, index }) => (
          <GridItem
            key={photo.id}
            columnSpan={isNarrow ? "full" : photoColumnSpan(photo)}
          >
            <PhotoThumbnail
              photo={photo}
              index={index}
              onClick={onOpen}
              liveBadgeLabel={liveBadgeLabel}
              videoBadgeLabel={videoBadgeLabel}
            />
          </GridItem>
        ))}
      </Grid>
    </section>
  );
}
